import { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ChatIcon from "@mui/icons-material/ChatBubbleOutline";
import dayjs from "dayjs";
import AppBackService from "../services/appback";
import { RootContext } from "../context/root-provider";
import { postLiveLinkToChat } from "../services/zoomMeeting";
import { apiErrorMessage, copyToClipboard } from "../services/utilities";
import { systemLabel } from "../liveTap/liveSystem";

/**
 * TT-1110 — public view-only links for a live tapestry, managed from the
 * host's Live Tap page. A link is a token URL anyone can open with no tapp
 * account; it shows this one tapestry (transcript stream, scene board, the
 * catalog the renderer needs) and nothing else, and it never advances the
 * meeting — viewers watch what the host produces.
 *
 * The link snapshots the rendering system the host has chosen at mint time
 * (the choice lives only in this browser's localStorage, so a viewer can only
 * learn it from the link). Flipping systems mid-meeting means minting again.
 * Links expire on their own after 7 days; revoke sooner from here.
 *
 * Inside a Zoom meeting (TT-1207 F4) a link also posts straight to the
 * meeting's chat, which is where it was always headed.
 */

const styles = {
  titleRow: { display: "flex", alignItems: "center", gap: "12px" },
  closeBtn: { marginLeft: "auto", color: "var(--color-text-secondary)" },
  hint: { color: "var(--color-text-tertiary)", fontSize: "0.85rem", marginBottom: "12px" },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 10px",
    border: "1px solid var(--color-border-primary)",
    borderRadius: "8px",
    marginBottom: "8px",
  },
  url: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: "monospace",
    fontSize: "0.85rem",
    color: "var(--color-text-primary)",
  },
  meta: { color: "var(--color-text-tertiary)", fontSize: "0.75rem", whiteSpace: "nowrap" },
  iconBtn: { color: "var(--color-text-secondary)" },
  empty: { color: "var(--color-text-secondary)", padding: "12px 0" },
  error: { color: "var(--color-error)", fontSize: "0.85rem", marginTop: "8px" },
  copied: { color: "var(--color-accent-primary)", fontSize: "0.85rem", marginRight: "auto" },
};

const LiveLinkModal = ({ open, onClose, userId, docId, liveSystem, docTitle }) => {
  const { inMeeting } = useContext(RootContext);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The last thing done with a link: { token, kind: "copied" | "posted" }.
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    if (!userId || !docId) return;
    setLoading(true);
    setError("");
    const res = await AppBackService.listLiveLinks(userId, docId);
    setLoading(false);
    if (res?.error) {
      setError(apiErrorMessage(res, "Unable to load live links"));
      return;
    }
    // Revoked links stay in the store for the audit trail; the host only
    // manages the ones that still open.
    setLinks((res?.links || []).filter((li) => li.active));
  }, [docId, userId]);

  useEffect(() => {
    if (open) {
      setDone(null);
      load();
    }
  }, [load, open]);

  const handleCreate = useCallback(async () => {
    setBusy(true);
    setError("");
    const res = await AppBackService.mintLiveLink(userId, docId, { liveSystem });
    setBusy(false);
    if (res?.error) {
      setError(apiErrorMessage(res, "Unable to create a live link"));
      return;
    }
    // The mint response is the row; no need to re-list (which the server
    // audits, one row per link, as handing out tokens).
    setLinks((prev) => [res, ...prev]);
    // Copy straight away: the whole point is pasting it into the meeting chat.
    if (res?.url && (await copyToClipboard(res.url))) {
      setDone({ token: res.token, kind: "copied" });
    }
  }, [docId, liveSystem, userId]);

  const handleCopy = useCallback(async (link) => {
    if (await copyToClipboard(link.url)) {
      setDone({ token: link.token, kind: "copied" });
    }
  }, []);

  const handlePost = useCallback(
    async (link) => {
      setBusy(true);
      setError("");
      try {
        await postLiveLinkToChat(docTitle, link.url);
        setDone({ token: link.token, kind: "posted" });
      } catch (err) {
        setError(err.message);
      } finally {
        setBusy(false);
      }
    },
    [docTitle]
  );

  const handleRevoke = useCallback(
    async (link) => {
      setBusy(true);
      setError("");
      const res = await AppBackService.revokeLiveLink(userId, docId, link.token);
      setBusy(false);
      if (res?.error) {
        setError(apiErrorMessage(res, "Unable to revoke the link"));
        return;
      }
      setDone((d) => (d?.token === link.token ? null : d));
      setLinks((prev) => prev.filter((li) => li.token !== link.token));
    },
    [docId, userId]
  );

  const doneWith = (link, kind) => done?.token === link.token && done.kind === kind;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box sx={styles.titleRow}>
          <span>Share a live link</span>
          <IconButton size="small" onClick={onClose} sx={styles.closeBtn} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography sx={styles.hint}>
          Anyone with a link can watch {docTitle ? `“${docTitle}”` : "this Tapestry"} as it unfolds — no account needed,
          view only. A link shows the {systemLabel(liveSystem)} rendering you have chosen now and expires after 7 days.
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "16px" }}>
            <CircularProgress size={28} />
          </Box>
        ) : links.length ? (
          links.map((link) => (
            <Box key={link.token} sx={styles.row}>
              <Typography component="span" sx={styles.url} title={link.url}>
                {link.url}
              </Typography>
              <Typography component="span" sx={styles.meta}>
                {systemLabel(link.liveSystem)}
                {link.expiresAt ? ` · until ${dayjs(link.expiresAt).format("MMM D")}` : ""}
              </Typography>
              {inMeeting && (
                <Tooltip title={doneWith(link, "posted") ? "Posted" : "Post to the meeting chat"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handlePost(link)}
                      disabled={busy}
                      sx={styles.iconBtn}
                      aria-label="Post to the meeting chat">
                      <ChatIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={doneWith(link, "copied") ? "Copied" : "Copy link"}>
                <IconButton size="small" onClick={() => handleCopy(link)} sx={styles.iconBtn} aria-label="Copy link">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Revoke — the link stops working for everyone">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleRevoke(link)}
                    disabled={busy}
                    sx={styles.iconBtn}
                    aria-label="Revoke link">
                    <LinkOffIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))
        ) : (
          <Typography sx={styles.empty}>No live links yet.</Typography>
        )}
        {error && <Typography sx={styles.error}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        {done && (
          <Typography sx={styles.copied}>
            {done.kind === "posted" ? "Posted to the meeting chat" : "Link copied to the clipboard"}
          </Typography>
        )}
        <Button onClick={onClose}>Done</Button>
        <Button variant="contained" onClick={handleCreate} disabled={busy || loading || !userId || !docId}>
          {busy ? "Working…" : "Create link"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

LiveLinkModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string,
  docId: PropTypes.string,
  liveSystem: PropTypes.string,
  docTitle: PropTypes.string,
};

export default LiveLinkModal;
